import { ExportableReport } from '../models/ExportableReport';
import { Filter } from '../models/Filter';
import { TableColumn } from '../models/table/TableColumn';
import { exportToPDF } from './PDFExporter';
import { exportToCSV } from './CSVExporter';
import { getMusteringReportTotals } from '../services/reportService';
import { REPORTS_MUSTERING } from '../constants/menu-types';
import { Translator } from '../contexts/translator';
import { TranslationKeys } from '../contexts/TranslationKeys';
import { DialogProps } from '../components/dialog/DialogContext';
import { EXPORT_FORMATS } from '../constants/system-constants';

export const exportReport = (report: ExportableReport, format: string, translate: Translator, dialog: DialogProps) => {
    const [columnHeadings, rows, appliedFilter, musteringTotals] = [
        getColumnsHeadings(report.columns, translate),
        getReportData(report.rows, report.columns),
        getAppliedFilters(translate, report.filters),
        getMusteringTotals(report)
    ];

    if (format === EXPORT_FORMATS.PDF) {
        exportToPDF(
            report.filename,
            report.title,
            report.description,
            report.user,
            columnHeadings,
            rows,
            report.date,
            translate,
            appliedFilter,
            report.searchTerm,
            musteringTotals
        );
    }
    else if (format === EXPORT_FORMATS.XLS || format === EXPORT_FORMATS.CSV) {
        exportToCSV(
            report.filename,
            report.title,
            report.description,
            report.user,
            columnHeadings,
            rows,
            report.date,
            translate,
            appliedFilter,
            report.searchTerm,
            musteringTotals
        );
    }
    else {
        dialog.pushError(translate.byKey(TranslationKeys.unsupportedExportFormat));
    }
};

const getColumnsHeadings = (columns: TableColumn[], translate: Translator): any[] => {
    return columns.map((column) => {
            return column.active ? translate.byKey(column.translationKey) ?? column.heading : undefined;
        })
        .filter((heading) => {
            return !!heading;
        });
};

const getReportData = (rows: any[], columns: TableColumn[]): any[] => {
    let data: any = [];
    rows.forEach((row) => {
        let obj: any = [];
        columns.forEach((column) => {
            if (column.active) {
                obj.push(row[column.accessor]);
            }
        });
        data.push(obj);
    });
    return data;
};

const getAppliedFilters = (translate: Translator, filters?: Filter[]): string[] => {
    let appliedFilters: string[] = [];

    filters?.forEach((element) => {
        let filter = `${element.label} : `;
        let filterValues: any[] = [];
        element.selected.forEach((element) => {
            filterValues.push(element.label);
        });
        filter += filterValues.join(` ${translate.byKey(TranslationKeys.or)} `);
        appliedFilters.push(filter);
    });

    return appliedFilters;
};

const getMusteringTotals = async (report: ExportableReport) => {
    try {
        const siteFilter = report.filters?.find(filter => filter.key === "site.id")
        if (report.reportId && siteFilter && siteFilter.selected.length > 0 && report.menuId === REPORTS_MUSTERING) {
            const totals = await getMusteringReportTotals(siteFilter.selected[0].value, +report.reportId);
            return totals;
        }

        return undefined;
    } catch (error) {
        return undefined;
    }
}

/**
 * Converts an array of numbers (0–255) into a Uint8Array (byte array).
 * 
 * @param numbers - An array of numbers where each number must be between 0 and 255.
 * @returns A Uint8Array representing the byte array.
 * @throws Error if any number in the array is out of byte range.
 */
const numberArrayToByteArray = (numbers: number[]): Uint8Array => {
    // if (!numbers.every(num => Number.isInteger(num) && num >= 0 && num <= 255)) {
    //     throw new Error("All numbers must be integers between 0 and 255.");
    // }
    return new Uint8Array(numbers);
};

/**
 * Converts a Uint8Array (byte array) into a File object with .pfx MIME type.
 * 
 * @param byteArray - The byte array representing the contents of the file.
 * @param fileName - The desired name of the resulting file (including extension).
 * @returns A File object that can be used for further operations like downloading.
 */
const byteArrayToFile = (byteArray: any, fileName: string): File => {
    const blob = new Blob([byteArray], { type: 'application/x-pkcs12' }); // MIME type for .pfx
    return new File([blob], fileName, { type: 'application/x-pkcs12' });
};

/**
 * Triggers the browser's download prompt for a given File object.
 * 
 * @param file - The File object to be downloaded.
 */
const triggerDownload = (file: File): void => {
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Combines byte array conversion and download trigger to initiate a download
 * of a .pfx file with a given name.
 * 
 * @param byteArray - The byte array content to be downloaded.
 * @param desiredFileName - Optional custom name for the downloaded file. Defaults to 'certificate.pfx'.
 */
export const downloadPfxFileFromBytes = (certAsNumberArray: number[], desiredFileName: string = 'certificate.pfx'): void => {
    const byteArray = numberArrayToByteArray(certAsNumberArray);
    const file = byteArrayToFile(byteArray, desiredFileName);
    triggerDownload(file);
};

